/**
 * How long a show has been running, or ran for (spec §17.7).
 *
 * A show's duration is not a stored figure and should not become one: it is `startedAt` to
 * `endedAt`, and both are already on the show. Deriving it means it cannot disagree with the
 * timestamps, and a show whose end was recorded late is a show whose duration corrects itself.
 *
 * Null before the show starts. A scheduled show has no duration — it has a start time, which is
 * a different thing and is displayed as one.
 */
export function showDuration(
  show: { startedAt: string | null; endedAt: string | null },
  now: number,
): number | null {
  if (!show.startedAt) return null;
  const from = new Date(show.startedAt).getTime();
  const to = show.endedAt ? new Date(show.endedAt).getTime() : now;
  // A clock that has gone backwards — a machine waking from sleep, a corrected NTP offset —
  // should read as zero rather than as a negative duration counting up towards it.
  return Math.max(to - from, 0);
}

/**
 * `MM:SS`, or `H:MM:SS` once a show passes the hour.
 *
 * Minutes and seconds rather than hours and minutes, because the number is watched while a show
 * runs: a figure that only changes once a minute looks broken for the first sixty seconds of
 * every show. The hour is added only when there is one, so the common case stays two fields wide.
 */
export function formatDuration(milliseconds: number): string {
  const total = Math.floor(milliseconds / 1000);
  const seconds = total % 60;
  const minutes = Math.floor(total / 60) % 60;
  const hours = Math.floor(total / 3600);
  const pad = (value: number): string => String(value).padStart(2, "0");
  return hours > 0
    ? `${hours}:${pad(minutes)}:${pad(seconds)}`
    : `${pad(minutes)}:${pad(seconds)}`;
}
